/**
 * A custom pagination hook. Heavily inspired fom MaterialUI's.
 * Many props have been removed from MaterialUI's implementation for simplicity, though can easily be re-added as needed.
 *
 * @see https://github.com/mui-org/material-ui/blob/master/packages/material-ui-lab/src/Pagination/usePagination.js for more
 */
const usePagination = (props = {}) => {
  const {
    // Required
    currentPage,
    setCurrentPage,
    pageCount,
    // Optional
    boundaryCount = 1,
    disabled = false,
    showFirstButton = false,
    showLastButton = false,
    hideNextButton = false,
    hidePrevButton = false
  } = props

  /**
   * The number of "siblings" to render around a value in the middle of the list.
   */
  const SIBLING_COUNT = 1

  /**
   * Handle the user selecting one of the pages in the pagination list.
   *
   * @param {object} event
   * @param {string} value
   */
  const handleClick = (event, value) => {
    event.preventDefault()
    setCurrentPage(value)
  }

  /**
   * Create a range of values.
   *
   * @param {number} start
   * @param {number} end
   * @returns {object} a range of values, as an array of numbers, of length end - start + 1 from offset by start
   */
  const range = (start, end) => {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
  }

  // List from 1 to current, or the boundary; whatever's smaller
  const startPages = range(1, Math.min(boundaryCount, pageCount))

  // List from count - boundary + 1, or boundary + 1; whatever's bigger, to count
  const endPages = range(Math.max(pageCount - boundaryCount + 1, boundaryCount + 1), pageCount)

  const siblingsStart = Math.max(
    Math.min(currentPage - SIBLING_COUNT, pageCount - boundaryCount - SIBLING_COUNT * 2 - 1),
    boundaryCount + 2
  )

  const siblingsEnd = Math.min(
    Math.max(currentPage + SIBLING_COUNT, boundaryCount + SIBLING_COUNT * 2 + 2),
    endPages[0] - 2
  )

  /**
   * Generate the list of all items to render. Spreads a bunch of arrays to concatenate them.
   * // e.g. itemList = ['first', 'previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next', 'last']
   */
  const itemList = [
    // Previous button
    ...(showFirstButton ? ['first'] : []),
    ...(hidePrevButton ? [] : ['previous']),

    // Start pages
    ...startPages,

    // Start ellipsis
    ...(siblingsStart > boundaryCount + 2
      ? ['start-ellipsis']
      : boundaryCount + 1 < pageCount - boundaryCount
      ? [boundaryCount + 1]
      : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    ...(siblingsEnd < pageCount - boundaryCount - 1
      ? ['end-ellipsis']
      : pageCount - boundaryCount > boundaryCount
      ? [pageCount - boundaryCount]
      : []),

    // End pages
    ...endPages,

    // Next button
    ...(hideNextButton ? [] : ['next']),
    ...(showLastButton ? ['last'] : [])
  ]

  // Map the button type to its page number
  const buttonPage = (type) => {
    switch (type) {
      case 'first':
        return 1
      case 'previous':
        return currentPage - 1
      case 'next':
        return currentPage + 1
      case 'last':
        return pageCount
      default:
        return null
    }
  }

  /**
   * Map itemList to a list of objects that are now renderable as a <PaginationItem ...>
   */
  const items = itemList.map((item) => {
    return typeof item === 'number'
      ? // Render the props needed to render a "numeric" item in the list
        {
          onClick: (event) => {
            handleClick(event, item)
          },
          type: 'page',
          page: item,
          selected: item === currentPage,
          // Disabled iff disabled
          disabled
        }
      : // Render props for "non-numeric" items
        {
          onClick: (event) => {
            // Same handleClick handler, but map item to it's numeric value
            handleClick(event, buttonPage(item))
          },
          type: item,
          page: buttonPage(item),
          // Can't select previous button, next button, etc.
          selected: false,
          // Disabled if disabled OR there's no ellipsis in the list and current page is above the boundaryCount or we're on the first page
          disabled:
            disabled ||
            (item.indexOf('ellipsis') === -1 &&
              (item === 'next' || item === 'last' ? currentPage >= pageCount : currentPage <= 1))
        }
  })

  // Render items as an object
  return {
    items
  }
}

export default usePagination
